"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProfileService = void 0;
const errors_1 = require("../errors");
const service_1 = require("./service");
const memberSecurity_1 = require("../models/memberSecurity");
const profileMapper_1 = require("../utils/profileMapper");
const warnTemplates_1 = require("../utils/warnTemplates");
class ProfileService extends service_1.Service {
    constructor(transport, auth) {
        super(transport, auth);
    }
    async getProfile(userId) {
        const response = await this.transport.send('mi', [userId]);
        this.checkError(response, 'mi');
        // Raw result is flat array
        return profileMapper_1.ProfileMapper.mapProfile(response.data);
    }
    async getMentions(offset = 0, showPosts = false, limit = 100) {
        const response = await this.transport.send('mm', [0, 0, showPosts ? 1 : 0, offset, limit]);
        this.checkError(response, 'mm');
        return profileMapper_1.ProfileMapper.mapMentions(response.data, showPosts);
    }
    async getSelfProfile() {
        return this.getProfile(0);
    }
    async resolveUser(query) {
        const trimmed = query.trim();
        if (!trimmed) {
            throw new Error('Пустой запрос');
        }
        // 1. Проверяем, является ли запрос ссылкой на профиль.
        // Ищет паттерны типа showuser=123456, mid=123456, u=123456, /member.php?u=123456, /user/123456
        const showuserMatch = trimmed.match(/(?:showuser|mid|u)=(\d+)/i) ||
            trimmed.match(/\/member\.php\?u=(\d+)/i) ||
            trimmed.match(/\/user\/(\d+)/i);
        if (showuserMatch) {
            const userId = parseInt(showuserMatch[1], 10);
            return this.getProfile(userId);
        }
        // 2. Ищем пользователей по никнейму через "rm"
        const searchResult = await this.searchUsers(trimmed, 0, 0, 50);
        if (!searchResult.users || searchResult.users.length === 0) {
            throw new Error(`Пользователь "${trimmed}" не найден`);
        }
        // Сначала ищем точное совпадение по нику
        const exactMatch = searchResult.users.find(u => u.name.toLowerCase() === trimmed.toLowerCase());
        if (exactMatch) {
            return exactMatch;
        }
        // Если точного совпадения нет, берем первого найденного
        return searchResult.users[0];
    }
    async searchUsers(query, groupId = 0, offset = 0, limit = 20) {
        const response = await this.transport.send('rm', [groupId, query, offset, limit]);
        this.checkError(response, 'rm');
        const count = response.data[0];
        return {
            count,
            users: profileMapper_1.ProfileMapper.mapUserSearch(response.data)
        };
    }
    async getReputationHistory(userId, type = 2, offset = 0, limit = 20) {
        const response = await this.transport.send('mr', [userId, type, offset, limit]);
        this.checkError(response, 'mr');
        return profileMapper_1.ProfileMapper.mapReputationHistory(response.data);
    }
    async getHistory(offset = 0, limit = 20) {
        const response = await this.transport.send('mh', [offset, limit]);
        this.checkError(response, 'mh');
        return profileMapper_1.ProfileMapper.mapHistory(response.data);
    }
    /**
     * Modify reputation (vote for user or post).
     * Command: "mr" (MEMBER_REPUTATION) - 29293
     * @param targetUserId ID of the user whose reputation is being modified
     * @param direction 1 = increase (+1), 0 = decrease (-1)
     * @param postId ID of the forum post (0 for direct profile vote, -commentId for site comment)
     * @param reason Comment/reason for vote
     */
    async modifyReputation(targetUserId, direction, postId, reason) {
        const response = await this.transport.send('mr', [targetUserId, direction, postId, reason]);
        this.checkError(response, 'mr');
        return response.status === 0;
    }
    /**
     * Cancel/delete a reputation vote.
     * Command: "mr" (MEMBER_REPUTATION) - 29293
     * @param action 4 = regular cancel, 5 = cancel and punish
     * @param voteId Unique vote transaction ID
     * @param reason Optional reason/comment
     */
    async cancelReputationVote(action, voteId, reason = '') {
        const response = await this.transport.send('mr', [0, action, voteId, reason]);
        this.checkError(response, 'mr');
        return response.status === 0;
    }
    /**
     * Save user profile changes.
     * Command: "mp" (MEMBER_PROFILE_SAVE) - 28781
     * @param userId User ID
     * @param status Text status
     * @param signature Forum BBCode signature
     * @param aboutMe "About Me" description
     * @param birthday Birthday string (e.g. "DD-MM-YYYY" or empty)
     * @param gender Gender: 0 = unspecified, 1 = male, 2 = female
     * @param location Location string
     */
    async saveProfile(userId, status, signature, aboutMe, birthday = '', gender = 0, location = '') {
        const result = await this.saveProfileDetailed(userId, status, signature, aboutMe, birthday, gender, location);
        if (!result.ok) {
            throw new errors_1.FourPdaError(result.status, 'mp', result.message);
        }
        return true;
    }
    /**
     * Save profile with field-level errors (Android `mp` / 28781).
     */
    async saveProfileDetailed(userId, status, signature, aboutMe, birthday = '', gender = 0, location = '') {
        const response = await this.transport.send('mp', [
            userId,
            status,
            signature,
            aboutMe,
            birthday,
            gender,
            location,
        ]);
        if (response.status === 0) {
            return { ok: true, status: 0, message: '' };
        }
        const raw = response.data?.[0];
        const fieldErrors = Array.isArray(raw) ? {
            status: typeof raw[1] === 'string' ? raw[1] : undefined,
            signature: typeof raw[2] === 'string' ? raw[2] : undefined,
            aboutMe: typeof raw[3] === 'string' ? raw[3] : undefined,
            location: typeof raw[4] === 'string' ? raw[4] : undefined,
        } : undefined;
        const message = (Array.isArray(raw) && typeof raw[0] === 'string' && raw[0])
            ? raw[0]
            : 'Ошибка изменения профиля';
        return {
            ok: false,
            status: response.status ?? -1,
            message,
            fieldErrors,
        };
    }
    /**
     * MEMBER_SECURITY (`mu`, 30061).
     * Request: [action, memberId, flags/ts, sig, value]
     */
    async memberSecurity(action, memberId = 0, flags = 0, sig = '', value = '') {
        const response = await this.transport.send('mu', [action, memberId, flags, sig, value]);
        const status = response.status ?? -1;
        return {
            ok: status === 0,
            status,
            message: (0, memberSecurity_1.mapMemberSecurityMessage)(status, action),
        };
    }
    async changeEmail(password, newEmail) {
        return this.memberSecurity(memberSecurity_1.MemberSecurityAction.SetEmail, 0, 0, password, newEmail);
    }
    async changePassword(currentPassword, newPassword, endAllSessions = false) {
        return this.memberSecurity(memberSecurity_1.MemberSecurityAction.SetPassword, 0, endAllSessions ? 1 : 0, currentPassword, newPassword);
    }
    async changeLogin(userId, password, newLogin) {
        return this.memberSecurity(memberSecurity_1.MemberSecurityAction.SetLogin, userId, 0, password, newLogin);
    }
    /**
     * Request password reset email.
     * Command: `mu` (MEMBER_SECURITY) — action 5.
     * @param loginOrEmail Login on sign-in screen or e-mail on registration (Android: sig field).
     */
    async requestPasswordReset(loginOrEmail) {
        return this.memberSecurity(memberSecurity_1.MemberSecurityAction.RequestResetPassword, 0, 0, loginOrEmail.trim(), '');
    }
    /**
     * Complete password reset from e-mail link.
     * Command: `mu` (MEMBER_SECURITY) — action 6.
     * @param memberId User ID (`m` from link)
     * @param timestamp Link timestamp (`t`)
     * @param sig Link signature (`s`)
     * @param newPassword New password (entered twice in UI)
     */
    async resetPassword(memberId, timestamp, sig, newPassword) {
        return this.memberSecurity(memberSecurity_1.MemberSecurityAction.ResetPassword, memberId, timestamp, sig, newPassword);
    }
    /**
     * Resend account activation e-mail.
     * Command: `mu` (MEMBER_SECURITY) — action 0. Requires logged-in not-activated user.
     */
    async resendActivation() {
        return this.memberSecurity(memberSecurity_1.MemberSecurityAction.ResendActivation, 0, 0, '', '');
    }
    /**
     * Activate account from registration e-mail link.
     * Command: `mu` — action 1. Verified: `mu(1, m, t, s, "")`.
     */
    async activate(memberId, timestamp, sig) {
        return this.memberSecurity(memberSecurity_1.MemberSecurityAction.Activate, memberId, timestamp, sig, '');
    }
    /**
     * Confirm e-mail change from letter link.
     * Command: `mu` — action 3. Verified: `mu(3, m, t, s, p)` where `p` is new e-mail from link.
     */
    async confirmEmail(memberId, timestamp, sig, newEmail) {
        return this.memberSecurity(memberSecurity_1.MemberSecurityAction.ConfirmEmail, memberId, timestamp, sig, newEmail);
    }
    /**
     * Get moderation warning templates.
     * Command: "mw" (MEMBER_WARN) - 30573, Action = 0
     * @param targetUserId ID of the user
     * @param postId ID of the message (or 0)
     */
    async getWarnTemplates(targetUserId, postId = 0) {
        const response = await this.transport.send('mw', [targetUserId, 0, postId]);
        this.checkError(response, 'mw');
        return (0, warnTemplates_1.mapWarnTemplates)(response.data);
    }
    /**
     * Apply moderation warn (warning/ban/RO).
     * Command: "mw" (MEMBER_WARN) - 30573, Action = 1
     */
    async applyWarn(targetUserId, postId, reason, category, templateAction, flags, userMessage, roTime, banTime) {
        const response = await this.transport.send('mw', [
            targetUserId,
            1,
            postId,
            reason,
            category,
            templateAction,
            flags,
            userMessage,
            roTime,
            banTime
        ]);
        this.checkError(response, 'mw');
        return response.status === 0;
    }
    /**
     * Add a moderator note to the user profile.
     * Command: "mw" (MEMBER_WARN) - 30573, Action = 3
     */
    async addModeratorNote(targetUserId, note) {
        const response = await this.transport.send('mw', [targetUserId, 3, 0, note]);
        this.checkError(response, 'mw');
        return response.status === 0;
    }
    /**
     * Remove moderation warning/punishment.
     * Command: "mw" (MEMBER_WARN) - 30573, Action = 4
     */
    async removeWarn(targetUserId, reason) {
        const response = await this.transport.send('mw', [targetUserId, 4, 0, reason]);
        this.checkError(response, 'mw');
        return response.status === 0;
    }
    /**
     * Upload and update user avatar.
     * Command: "mv" (MEMBER_AVATAR_UPLOAD) - 30317
     * @param avatarData Base64 string of the image
     */
    async uploadAvatar(avatarData) {
        const response = await this.transport.send('mv', [avatarData]);
        this.checkError(response, 'mv');
        return response.status === 0;
    }
}
exports.ProfileService = ProfileService;
